import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchGamification, submitDailyAnswer } from "../../context/actions/gamification";

const LABELS = { a: "A", b: "B", c: "C", d: "D" };

function Countdown({ seconds }) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return (
    <span className="font-mono text-sm font-semibold text-[#294a93]">
      {h > 0 && `${h}h `}{m}m {String(s).padStart(2, "0")}s
    </span>
  );
}

export default function DailyQuestionCard({ question: initial, onPointsChanged }) {
  const [question, setQuestion]     = useState(initial);
  const [selected, setSelected]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]         = useState(null); // { correct, points_awarded, message }
  const [countdown, setCountdown]   = useState(initial?.seconds_to_reveal ?? 0);

  // Sync when parent refreshes data (intentionally keyed on id only)
  useEffect(() => {
    setQuestion(initial);
    setCountdown(initial?.seconds_to_reveal ?? 0);
    setResult(null);
    setSelected(initial?.answered ? (initial.my_answer ?? initial.selected_option ?? null) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.id]);

  // Live countdown ticker
  useEffect(() => {
    if (!countdown || countdown <= 0) return;
    const id = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [countdown]);

  if (!question) return null;

  const isAnswered  = question.answered || !!result;
  const isRevealed  = countdown <= 0;
  const canAnswer   = !isAnswered && !isRevealed;

  const handleSubmit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    const res = await submitDailyAnswer(selected);
    setSubmitting(false);
    if (res.ok) {
      setResult(res.data);
      if (res.data.correct) toast.success("Correct! +5 points 🎉");
      else toast.info("Not quite — check the reveal at 9pm!");
      // Refresh gamification so points update
      const fresh = await fetchGamification();
      if (fresh && onPointsChanged) onPointsChanged(fresh);
    } else {
      toast.error(res.message);
    }
  };

  const correctOption = question.correct_option; // only set after reveal
  const userPick      = result ? selected : (question.answered ? (question.my_answer ?? question.selected_option) : selected);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#294a93] to-[#3b60c4] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-white text-lg">🧠</span>
          <span className="text-white text-sm font-semibold">Question of the Day</span>
        </div>
        {!isRevealed && countdown > 0 && (
          <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2 py-1">
            <span className="text-white text-xs opacity-80">Reveals in</span>
            <Countdown seconds={countdown} />
          </div>
        )}
        {isRevealed && (
          <span className="text-xs bg-white/20 text-white px-2 py-1 rounded-lg">Answer Revealed</span>
        )}
      </div>

      <div className="p-4">
        {/* Course badge */}
        {question.course_name && (
          <span className="inline-block text-xs bg-blue-50 text-[#294a93] font-medium px-2 py-0.5 rounded-full mb-3">
            {question.course_name}
          </span>
        )}

        {/* Question text */}
        <p className="text-sm font-medium text-gray-800 mb-4 leading-relaxed">
          {question.question}
        </p>

        {/* Options */}
        <div className="space-y-2">
          {["a", "b", "c", "d"].map((opt) => {
            const isUserPick  = userPick === opt;
            const isCorrect   = correctOption === opt;
            const showCorrect = isRevealed && isCorrect;
            const showWrong   = isRevealed && isUserPick && !isCorrect;

            let cls =
              "w-full text-left px-3 py-2.5 rounded-xl border text-sm transition-all duration-150 flex items-center gap-3";

            if (showCorrect)
              cls += " bg-emerald-50 border-emerald-400 text-emerald-800 font-medium";
            else if (showWrong)
              cls += " bg-red-50 border-red-300 text-red-700";
            else if (!isRevealed && isUserPick)
              cls += " bg-[#FEF0EB] border-[#d15424] text-[#d15424] font-medium";
            else if (canAnswer && selected === opt)
              cls += " bg-[#FEF0EB] border-[#d15424] text-[#d15424] font-medium";
            else
              cls += " bg-gray-50 border-gray-200 text-gray-700 hover:border-[#d15424] hover:bg-[#FEF0EB]";

            return (
              <button
                key={opt}
                className={cls}
                disabled={!canAnswer}
                onClick={() => canAnswer && setSelected(opt)}
              >
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                    ${showCorrect ? "bg-emerald-500 text-white" : showWrong ? "bg-red-400 text-white" : "bg-gray-200 text-gray-600"}`}
                >
                  {LABELS[opt]}
                </span>
                <span>{question[`option_${opt}`]}</span>
                {showCorrect && <span className="ml-auto">✓</span>}
                {showWrong   && <span className="ml-auto">✗</span>}
              </button>
            );
          })}
        </div>

        {/* Explanation (after reveal) */}
        {isRevealed && question.explanation && (
          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-3">
            <p className="text-xs font-semibold text-[#294a93] mb-1">Explanation</p>
            <p className="text-xs text-gray-600 leading-relaxed">{question.explanation}</p>
          </div>
        )}

        {/* Submit / result */}
        {canAnswer && (
          <button
            onClick={handleSubmit}
            disabled={!selected || submitting}
            className="mt-4 w-full py-2.5 rounded-xl bg-[#d15424] text-white text-sm font-semibold
              disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#b8451d] transition-colors"
          >
            {submitting ? "Submitting…" : "Submit Answer"}
          </button>
        )}

        {result && (
          <div
            className={`mt-3 rounded-xl px-3 py-2 text-sm font-medium text-center
              ${result.correct ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}
          >
            {result.message}
          </div>
        )}

        {isAnswered && !result && !isRevealed && (
          <p className="mt-3 text-center text-xs text-gray-400">
            Answer revealed at 9pm WAT — check back then!
          </p>
        )}

        {isRevealed && !isAnswered && !result && (
          <p className="mt-3 text-center text-xs text-gray-400">
            This question is now closed.
          </p>
        )}
      </div>
    </div>
  );
}
